import { useEffect, useState } from "react";
import { ClipboardList, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const blankRole = () => ({ id: "", role_name: "", days_per_week: 5, since: new Date().toISOString().slice(0, 10), notes: "" });

export const PlannedRosterPanel = ({ clientId, canEdit, onSaved }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEditing(false);
    api.get(`/operations-center/${clientId}/planned-roster`).then((r) => { setData(r.data); setError(""); }).catch((e) => setError(formatApiError(e)));
  }, [clientId]);

  const startEdit = () => {
    setRows((data?.roles || []).map((r) => ({ id: r.id, role_name: r.role_name, days_per_week: r.days_per_week, since: r.since, notes: r.notes || "" })));
    setEditing(true);
  };
  const update = (index, patch) => setRows((current) => current.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await api.put(`/operations-center/${clientId}/planned-roster`, { roles: rows });
      setData(response.data);
      setEditing(false);
      setError("");
      onSaved?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="operations-planned-roster" data-testid="planned-roster-panel">
      <div className="operations-panel-heading">
        <ClipboardList size={18} />
        <div>
          <h3>Planned roster</h3>
          <p data-testid="planned-roster-label">{data ? `${data.label} · from the SY 2026-27 revenue projection and position board; a role counts as filled only while an active assignment covers it.` : "Loading…"}</p>
        </div>
        {canEdit && data && !editing && <button type="button" className="button button-secondary" data-testid="planned-roster-edit" onClick={startEdit}><Pencil size={14} /> Edit planned roster</button>}
      </div>
      {error && <p className="form-message error-message" data-testid="planned-roster-error">{error}</p>}
      {data && !editing && (
        <ul className="planned-roster-list" data-testid="planned-roster-list">
          {data.roles.map((role) => (
            <li key={role.id} data-testid={`planned-role-${role.id}`} className={role.status === "Unfilled" ? "unfilled" : "filled"}>
              <span className={`status-chip ${role.status === "Unfilled" ? "status-inactive" : "status-active"}`}>{role.status}</span>
              <span>{role.label}</span>
              <small className="pairing-meta">{data.sources[role.source] || role.source}{role.notes ? ` · ${role.notes}` : ""}</small>
            </li>
          ))}
          {!data.roles.length && <li className="pairing-meta" data-testid="planned-roster-empty">No planned roles for this client yet.</li>}
          {data.unplanned_assignments?.length > 0 && <li className="pairing-meta" data-testid="planned-roster-unplanned">Also placed here, not in the plan: {data.unplanned_assignments.map((a) => `${a.person}${a.role ? ` (${a.role})` : ""}`).join(", ")}</li>}
        </ul>
      )}
      {editing && (
        <form className="planned-roster-form" data-testid="planned-roster-form" onSubmit={save}>
          {rows.map((row, index) => (
            <div className="planned-roster-row" key={row.id || `new-${index}`} data-testid={`planned-roster-row-${index}`}>
              <input data-testid={`planned-role-name-${index}`} required placeholder="Role (e.g. COTA)" value={row.role_name} onChange={(e) => update(index, { role_name: e.target.value })} />
              <label>Days/wk<input data-testid={`planned-role-days-${index}`} type="number" min={1} max={5} value={row.days_per_week} onChange={(e) => update(index, { days_per_week: Number(e.target.value) })} /></label>
              <label>Since<input data-testid={`planned-role-since-${index}`} type="date" value={row.since} onChange={(e) => update(index, { since: e.target.value })} /></label>
              <input data-testid={`planned-role-notes-${index}`} placeholder="Notes" value={row.notes} onChange={(e) => update(index, { notes: e.target.value })} />
              <button type="button" className="projection-icon-button" aria-label="Remove role" data-testid={`planned-role-remove-${index}`} onClick={() => setRows((current) => current.filter((_, i) => i !== index))}><Trash2 size={14} /></button>
            </div>
          ))}
          <div className="planned-roster-actions">
            <button type="button" className="button button-secondary" data-testid="planned-role-add" onClick={() => setRows((current) => [...current, blankRole()])}><Plus size={14} /> Add role</button>
            <button type="submit" className="button button-primary" data-testid="planned-roster-save" disabled={busy}>{busy ? "Saving…" : "Save planned roster"}</button>
            <button type="button" className="button button-secondary" data-testid="planned-roster-cancel" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
};
